import { useState, useEffect } from 'react';          // useRef ya no hace falta
import { onAuthStateChanged } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { PLAN_LIMITS, STRIPE_COLLECTIONS, PRICE_IDS } from './stripePlansConfig';

// ── Constantes ────────────────────────────────────────────────────────────────
const ACTIVE_STATUSES = ['active', 'trialing'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const resolvePlanKeyFromPriceId = (priceId) => {
  if (!priceId) return null;
  const match = Object.entries(PRICE_IDS).find(([, id]) => id === priceId);
  return match ? match[0] : null;
};

const extractPriceId = (subData) => {
  if (subData?.items?.[0]?.price?.id)     return subData.items[0].price.id;
  if (typeof subData?.price === 'string') return subData.price;
  if (subData?.price?.id)                 return subData.price.id;
  if (subData?.prices?.[0]?.id)           return subData.prices[0].id;
  if (subData?.items?.[0]?.plan?.id)      return subData.items[0].plan.id;
  return null;
};

const resolveIsActive = (subData) => {
  if (!subData)                                         return false;
  if (!ACTIVE_STATUSES.includes(subData.status))        return false;

  const periodEnd =
    subData.current_period_end?.seconds ??
    subData.current_period_end?._seconds ??
    null;

  if (periodEnd !== null && Math.floor(Date.now() / 1000) > periodEnd) {
    console.warn(
      `[useSubscription] status="${subData.status}" pero current_period_end ya expiró.`,
      `cancel_at_period_end=${subData.cancel_at_period_end}`
    );
    return false;
  }

  return true;
};

// FIX: doc.data() se invocaba dos veces por comparación dentro del sort
const getDocTimestamp = (doc) => {
  const d = doc.data();
  return d?.current_period_start?.seconds ?? d?.created?.seconds ?? 0;
};

const sortByMostRecent = (docs) =>
  [...docs].sort((a, b) => getDocTimestamp(b) - getDocTimestamp(a));

// ── Estado base (sin campo loading para evitar confusión al hacer spread) ─────
const FREE_DATA = {
  isActive:          false,
  planKey:           'free',
  historicPlanKey:   'free',
  planName:          PLAN_LIMITS.free.name,
  planLimits:        PLAN_LIMITS.free,
  cancelAtPeriodEnd: false,
  subscription:      null,
  error:             null,
};

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useSubscription() {
  const [state, setState] = useState({ ...FREE_DATA, loading: true });

  useEffect(() => {
    // Variables locales en lugar de refs: el efecto corre una sola vez (deps=[])
    // y el closure de cleanup captura siempre el valor actual de unsubSub.
    let unsubSub = null;

    const cleanupSub = () => { unsubSub?.(); unsubSub = null; };
    const resetToFree = (loading = false) => setState({ ...FREE_DATA, loading });

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      cleanupSub();

      if (!user) {
        resetToFree(false);
        return;
      }

      const subsRef = collection(
        db,
        STRIPE_COLLECTIONS.customers,
        user.uid,
        'subscriptions'
      );

      unsubSub = onSnapshot(
        subsRef,
        (snapshot) => {
          console.log('[useSubscription] total docs:', snapshot.size);

          if (snapshot.empty) {
            resetToFree(false);
            return;
          }

          // FIX: d.data() llamado una vez por doc para el log
          snapshot.docs.forEach((d) => {
            const data = d.data();
            console.log(
              '[useSubscription] sub doc:', d.id,
              '| status:', data?.status,
              '| cancel_at_period_end:', data?.cancel_at_period_end,
              '| period_end:', data?.current_period_end?.seconds
            );
          });

          const activeDocs = snapshot.docs.filter((d) =>
            ACTIVE_STATUSES.includes(d.data()?.status)
          );

          // ── Sin docs activos: solo conservamos historicPlanKey para la UI ──
          if (!activeDocs.length) {
            const latestDoc  = sortByMostRecent(snapshot.docs)[0];
            const latestData = latestDoc.data();
            const priceId    = extractPriceId(latestData);
            const historicKey = resolvePlanKeyFromPriceId(priceId) ?? 'free';

            console.log('[useSubscription] Sin docs activos. historicPlanKey:', historicKey);

            setState({
              ...FREE_DATA,
              historicPlanKey: historicKey,
              subscription: { id: latestDoc.id, ...latestData, price: priceId ?? null, prices: undefined },
              loading: false,
            });
            return;
          }

          // ── Doc activo más reciente ───────────────────────────────────────
          const subDoc            = sortByMostRecent(activeDocs)[0];
          const subData           = subDoc.data();
          const isActive          = resolveIsActive(subData);
          const cancelAtPeriodEnd = subData?.cancel_at_period_end === true;
          const priceId           = extractPriceId(subData);
          const resolvedKey       = resolvePlanKeyFromPriceId(priceId) ?? 'free';
          const planLimits        = PLAN_LIMITS[resolvedKey] ?? PLAN_LIMITS.free;

          console.log(
            '[useSubscription] priceId:', priceId,
            '→ resolvedKey:', resolvedKey,
            '| isActive:', isActive,
            '| cancelAtPeriodEnd:', cancelAtPeriodEnd
          );

          setState({
            isActive,
            planKey:           isActive ? resolvedKey : 'free',
            historicPlanKey:   resolvedKey,
            planName:          isActive ? planLimits.name : PLAN_LIMITS.free.name,
            planLimits:        isActive ? planLimits : PLAN_LIMITS.free,
            cancelAtPeriodEnd,
            subscription: { id: subDoc.id, ...subData, price: priceId ?? null, prices: undefined },
            loading: false,
            error:   null,
          });
        },
        (err) => {
          console.error('[useSubscription] Firestore error:', err);
          setState((prev) => ({ ...prev, loading: false, error: err }));
        }
      );
    });

    return () => {
      cleanupSub();
      unsubAuth();
    };
  }, []);

  return state;
}